import React, { useMemo, useState } from "react";
import { Card, StatCard } from "../components/UI";

const READY_SYNC_HOURS = 48;

function formatDate(value) {
  if (!value) return "No data";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "No data";
  return d.toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" });
}

function hoursSince(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 3_600_000);
}

function statusStyle(status) {
  const value = String(status || "").toLowerCase();
  if (value.includes("noncompliant") || value.includes("error") || value.includes("fail") || value.includes("conflict")) return { color: "var(--danger)", bg: "#ef444418" };
  if (value.includes("success") || value.includes("succeeded") || value.includes("compliant")) return { color: "var(--success)", bg: "#22c55e18" };
  if (value.includes("pending") || value.includes("unknown")) return { color: "var(--warn)", bg: "#f59e0b18" };
  if (value.includes("notapplicable")) return { color: "var(--muted)", bg: "rgba(255,255,255,0.06)" };
  if (value.includes("missing")) return { color: "var(--danger)", bg: "#ef444418" };
  return { color: "var(--muted)", bg: "rgba(255,255,255,0.06)" };
}

function isPassingStatus(status) {
  const value = String(status || "").toLowerCase();
  if (value.includes("noncompliant")) return false;
  return value.includes("success") || value.includes("succeeded") || value.includes("compliant");
}

function StatusPill({ status }) {
  const s = statusStyle(status);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", borderRadius: 999, padding: "4px 9px", background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}>
      {status || "Unknown"}
    </span>
  );
}

function readinessFor(device) {
  const syncHours = hoursSince(device?.lastSyncDateTime);
  const recentSync = syncHours !== null && syncHours <= READY_SYNC_HOURS;
  const statuses = device?.policies || [];
  const required = statuses.filter(p => p.required !== false);
  const foundRequired = required.filter(p => p.status && p.status !== "Missing");
  const succeeded = required.filter(p => isPassingStatus(p.status));
  const failed = required.filter(p => /error|fail|conflict|noncompliant/i.test(String(p.status || "")));

  if (!device) return { label: "Search required", color: "var(--muted)", detail: "Search for a hostname, serial, username, or UPN." };
  if (!recentSync) return { label: "Needs review", color: "var(--warn)", detail: "Intune check-in is stale or unavailable." };
  if (device.onlyProvisioningUsersSeen) return { label: "Needs review", color: "var(--warn)", detail: "Only the provisioning account was found in recent logon data. Confirm the real user has signed in and synced." };
  if (!device.currentUser?.userPrincipalName) return { label: "Needs review", color: "var(--warn)", detail: "No current user could be resolved for this device." };
  if (failed.length) return { label: "Not ready", color: "var(--danger)", detail: "One or more required policies reports an error, failure, or conflict." };
  if (succeeded.length === required.length && required.length > 0) {
    return { label: "Ready to move", color: "var(--success)", detail: "Required EAP-TLS policies are compliant for the current user on this device." };
  }
  if (foundRequired.length === required.length && succeeded.length > 0) {
    return { label: "Probably ready", color: "var(--warn)", detail: "All required policies were found, but at least one is pending or unknown." };
  }
  return { label: "Needs review", color: "var(--warn)", detail: "One or more required policies was not found in the latest Intune status data." };
}

function DeviceSummary({ device }) {
  if (!device) return null;
  const syncHours = hoursSince(device.lastSyncDateTime);
  const currentUser = device.currentUser || {};
  const primaryUser = device.primaryUser || {};
  const currentUserSub = currentUser.lastLogOnDateTime
    ? `Last logon ${formatDate(currentUser.lastLogOnDateTime)}`
    : (currentUser.displayName || "No logon data");
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(140px, 1fr))", gap: 12, marginTop: 18 }}>
      <StatCard label="Device" value={device.deviceName || "Unknown"} sub={device.serialNumber || "No serial"} />
      <StatCard label="Current User" value={currentUser.userPrincipalName || "Unknown"} sub={currentUserSub} color={device.onlyProvisioningUsersSeen ? "var(--warn)" : "var(--text)"} />
      <StatCard label="Primary User" value={primaryUser.userPrincipalName || device.userPrincipalName || "Unknown"} sub={primaryUser.displayName || device.userDisplayName || "Intune primary"} />
      <StatCard label="Compliance" value={device.complianceState || "Unknown"} sub={device.managementState || "No state"} />
      <StatCard label="Last Sync" value={syncHours === null ? "No data" : `${syncHours}h ago`} sub={formatDate(device.lastSyncDateTime)} color={syncHours !== null && syncHours <= READY_SYNC_HOURS ? "var(--success)" : "var(--warn)"} />
    </div>
  );
}

function PolicySummary({ policies = [] }) {
  const required = policies.filter(p => p.required !== false);
  const compliant = required.filter(p => isPassingStatus(p.status)).length;
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
      <span style={{ border: "1px solid var(--border)", borderRadius: 999, padding: "5px 10px", fontSize: 12, color: "var(--muted)", background: "var(--bg3)" }}>
        {compliant}/{required.length} required policies compliant
      </span>
      {required.map(policy => {
        const s = statusStyle(policy.status);
        return (
          <span key={policy.name} title={policy.name} style={{ border: `1px solid ${s.color}55`, borderRadius: 999, padding: "5px 10px", fontSize: 12, color: s.color, background: s.bg, maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {policy.name.replace(/^CW\s+/i, "")}
          </span>
        );
      })}
    </div>
  );
}

export default function EapTlsMigration() {
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const selectedDevice = result?.devices?.[0] || null;
  const readiness = useMemo(() => readinessFor(selectedDevice), [selectedDevice]);
  const selectedSyncHours = hoursSince(selectedDevice?.lastSyncDateTime);

  const runSearch = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (q.length < 3) {
      setError("Enter at least 3 characters.");
      return;
    }
    setLoading(true);
    setError("");
    setSyncStatus("");
    setResult(null);
    try {
      const res = await fetch(`/api/eap-tls-readiness?q=${encodeURIComponent(q)}`, { cache: "no-store" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setResult(data);
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const syncSelectedDevice = async () => {
    if (!selectedDevice?.id) return;
    if (!window.confirm(`Request an Intune sync for ${selectedDevice.deviceName || selectedDevice.serialNumber || "this device"}?`)) return;
    setSyncing(true);
    setSyncStatus("");
    setError("");
    try {
      const res = await fetch("/api/eap-tls-sync-device", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          deviceId: selectedDevice.id,
          deviceName: selectedDevice.deviceName || null,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      setSyncStatus(`Intune sync requested ${formatDate(data.requestedAt)}. Re-check this device after Intune has reported back.`);
    } catch (err) {
      setSyncStatus("");
      setError(String(err.message || err));
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 20, alignItems: "flex-start", marginBottom: 22 }}>
        <div>
          <div style={{ fontSize: 11, color: "var(--accent)", textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 700, marginBottom: 8 }}>Temporary workflow</div>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 30, color: "var(--text)", margin: 0, letterSpacing: 0 }}>EAP-TLS SSID Migration Readiness</h1>
          <p style={{ color: "var(--muted)", fontSize: 14, maxWidth: 760, lineHeight: 1.55, marginTop: 8 }}>
            Search a user, hostname, serial, or UPN before removing a legacy Wi-Fi profile in ScreenConnect.
          </p>
        </div>
        <div style={{ background: "rgba(79,156,249,0.08)", border: "1px solid rgba(79,156,249,0.22)", borderRadius: 10, padding: "12px 14px", minWidth: 210 }}>
          <div style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>Legacy SSIDs</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 13 }}>
            <span><strong style={{ color: "var(--text)" }}>CRE</strong> <span style={{ color: "var(--muted)" }}>CCW / Wyndham</span></span>
            <span><strong style={{ color: "var(--text)" }}>Christway</strong> <span style={{ color: "var(--muted)" }}>CCK / Kingston</span></span>
          </div>
        </div>
      </div>

      <Card style={{ marginBottom: 18 }}>
        <form onSubmit={runSearch} style={{ display: "flex", gap: 10 }}>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search hostname, serial, username, or UPN"
            style={{ flex: 1, background: "var(--bg3)", border: "1px solid var(--border)", color: "var(--text)", borderRadius: 8, padding: "12px 14px", fontSize: 14, outline: "none" }}
          />
          <button disabled={loading} style={{ background: loading ? "var(--bg3)" : "var(--accent)", border: "1px solid var(--accent)", color: "#fff", borderRadius: 8, padding: "0 18px", fontSize: 13, fontWeight: 700, cursor: loading ? "wait" : "pointer", minWidth: 120 }}>
            {loading ? "Checking..." : "Check"}
          </button>
        </form>
        {error && <div style={{ color: "var(--danger)", marginTop: 12, fontSize: 13, fontWeight: 600 }}>{error}</div>}
      </Card>

      {result && !selectedDevice && (
        <Card>
          <h2 style={{ margin: 0, color: "var(--text)", fontSize: 18 }}>No Intune device found</h2>
          <p style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.55 }}>Try the full hostname, serial number, or user principal name.</p>
        </Card>
      )}

      {selectedDevice && (
        <>
          <Card style={{ borderColor: readiness.color, marginBottom: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 18, alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 10, color: "var(--muted)", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>Readiness</div>
                <div style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 800, color: readiness.color }}>{readiness.label}</div>
                <div style={{ color: "var(--muted)", fontSize: 13, marginTop: 4 }}>{readiness.detail}</div>
                <PolicySummary policies={selectedDevice.policies || []} />
              </div>
              <div style={{ textAlign: "right", color: "var(--muted)", fontSize: 12, lineHeight: 1.6 }}>
                <div style={{ marginBottom: 10 }}>
                  <div>Last Intune sync</div>
                  <div style={{ color: selectedSyncHours !== null && selectedSyncHours <= READY_SYNC_HOURS ? "var(--success)" : "var(--warn)", fontWeight: 800, marginTop: 2 }}>
                    {selectedSyncHours === null ? "No data" : `${selectedSyncHours}h ago`}
                  </div>
                  <div>{formatDate(selectedDevice.lastSyncDateTime)}</div>
                </div>
                <div>Next step when ready</div>
                <div style={{ color: "var(--text)", fontWeight: 700, marginTop: 4 }}>Remove only the legacy SSID currently in use.</div>
                <div style={{ marginTop: 6 }}>CRE = CCW / Wyndham</div>
                <div>Christway = CCK / Kingston</div>
                <button
                  onClick={syncSelectedDevice}
                  disabled={syncing || !selectedDevice.id}
                  style={{ marginTop: 12, background: syncing ? "var(--bg3)" : "var(--accent)", border: "1px solid var(--accent)", color: "#fff", borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 750, cursor: syncing ? "wait" : "pointer" }}
                >
                  {syncing ? "Requesting sync..." : "Sync With Intune"}
                </button>
              </div>
            </div>
            {syncStatus && (
              <div style={{ marginTop: 12, color: "var(--success)", background: "#22c55e14", border: "1px solid #22c55e44", borderRadius: 8, padding: "9px 12px", fontSize: 12, fontWeight: 650 }}>
                {syncStatus}
              </div>
            )}
            <DeviceSummary device={selectedDevice} />
          </Card>

          {result.devices.length > 1 && (
            <Card style={{ marginBottom: 18, borderColor: "rgba(245,158,11,0.35)" }}>
              <div style={{ color: "var(--warn)", fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Multiple Intune devices matched</div>
              <div style={{ color: "var(--muted)", fontSize: 12 }}>Showing the first match. Search a more exact hostname or serial if this is not the target device.</div>
            </Card>
          )}

          <Card>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <h2 style={{ margin: 0, color: "var(--text)", fontSize: 17 }}>Required Intune Policy Status</h2>
              <div style={{ color: "var(--muted)", fontSize: 11 }}>Graph checked {formatDate(result.checkedAt)}</div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Policy</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>User</th>
                    <th style={thStyle}>Last Reported</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedDevice.policies || []).map(policy => (
                    <tr key={policy.name} style={{ borderBottom: "1px solid var(--border)" }}>
                      <td style={tdStyle}>
                        <div style={{ color: "var(--text)", fontWeight: 650 }}>{policy.name}</div>
                        {!policy.policyId && <div style={{ color: "var(--danger)", fontSize: 11, marginTop: 3 }}>Policy not found by configured display name</div>}
                      </td>
                      <td style={tdStyle}>{policy.kind || "Policy"}</td>
                      <td style={tdStyle}><StatusPill status={policy.status || "Missing"} /></td>
                      <td style={tdStyle}>{policy.userPrincipalName || policy.userName || "—"}</td>
                      <td style={tdStyle}>{formatDate(policy.lastReportedDateTime)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

const thStyle = {
  padding: "10px 12px",
  fontSize: 10,
  color: "var(--muted)",
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  fontWeight: 700,
  background: "var(--bg3)",
  borderBottom: "1px solid var(--border)",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const tdStyle = {
  padding: "12px",
  color: "var(--muted)",
  fontSize: 12,
  verticalAlign: "top",
};
